import { useSelector, useDispatch } from 'react-redux';
import EntityHelper from '../../../storage/classes/Entity';
import {
	IEntityHelper,
	EntityHelperOpts,
	entityHelperDefaultOpts
} from '../../../storage';
import {
	getLicenseCollection,
	LicenseId,
	LicenseIds,
	LicenseId_Some,
	LicenseEntity,
	LicenseEntities,
	LicenseEntity_Some,
	LicenseEntityPatch_Some,
	LicenseCollection,
	LicenseCollectionState,
	ILicenseActions,
	licenseActions,
	LicenseActionTypes
} from '..';
import {
	readLicenses,
	writeLicenses,
	ReadLicensesRequest,
	WriteLicensesRequest,
	LicenseApiOperation,
	ReadLicensesAllRequest,
	readLicensesAll
} from '../apis';
import { UseCtx } from '../../../config/hooks';
import { ContextEntities, ServiceEntities } from '../collections';
import { isObjectStatusActive, LicenseServiceType } from '../models';

/**
 * License helper interface
 *
 * @export
 * @interface ILicenseHelper
 * @extends {IEntityHelper}
 */
export interface ILicenseHelper extends IEntityHelper {
	// customProperty: any;
	// customMethod(): any;
	// Custom functions
}

/**
 * License helper options interface
 *
 * @export
 * @interface LicenseHelperOpts
 * @extends {EntityHelperOpts}
 */
export interface LicenseHelperOpts extends EntityHelperOpts {
	// customOpt: any;
}

const licenseHelperOpts: LicenseHelperOpts = {
	...entityHelperDefaultOpts,
	...{}
};

/**
 * License helper
 *
 * @export
 * @class LicenseHelper
 * @extends {EntityHelper<LicenseCollection, LicenseActionTypes, LicenseActions, LicenseEntity, LicenseEntities, LicenseEntity_Some, LicenseEntityPatch_Some, LicenseId, LicenseIds, LicenseId_Some, LicenseCollectionState, LicenseHelperOpts>}
 * @implements {ILicenseHelper}
 */
export class LicenseHelper
	extends EntityHelper<
		LicenseCollection,
		LicenseActionTypes,
		ILicenseActions,
		LicenseEntity,
		LicenseEntities,
		LicenseEntity_Some,
		LicenseEntityPatch_Some,
		LicenseId,
		LicenseIds,
		LicenseId_Some,
		LicenseCollectionState,
		LicenseHelperOpts
	>
	implements ILicenseHelper {
	constructor() {
		super(
			useSelector(getLicenseCollection),
			licenseActions,
			useDispatch(),
			licenseHelperOpts
		);
		this.collection = useSelector(getLicenseCollection);
		this.dispatch = useDispatch();
	}

	lastSuccess(operationId: LicenseApiOperation, requestId: string = 'default') {
		return this.filter(
			entity =>
				!!entity.__state?.api?.operations?.[operationId]?.[requestId]?.success
					?.last?.dt
		).reverse()[0]?.__state?.api?.operations?.[operationId]?.[requestId]
			?.success?.last?.dt;
	}

	async read(
		ctx: UseCtx<any>,
		params: Partial<ReadLicensesRequest> = {},
		callback?: any
	): Promise<LicenseEntities> {
		if (!ctx.app.user.active()?.userId) return [];
		//params.modifiedFrom =
		//	params.modifiedFrom || this.lastSuccess(LicenseApiOperation.readLicenses);

		let request: ReadLicensesRequest = {
			...params,
			...{
				userId: ctx.app.user.active()?.userId || ''
			}
		};
		if (request.userId === '') return [];

		let entities: LicenseEntities = await readLicenses(ctx, request)
			.then((entities: LicenseEntities) => {
				if (callback) callback(entities);
				return entities;
			})
			.catch(e => {
				if (callback) callback(e);
				return [];
			});
		return entities;
	}

	async readAll(
		ctx: UseCtx<any>,
		params: Partial<ReadLicensesAllRequest> = {},
		callback?: any
	): Promise<LicenseEntities> {
		if (!ctx.app.user.active()?.userId) return [];
		//params.modifiedFrom =
		//	params.modifiedFrom || this.lastSuccess(UserApiOperation.readUsersAll);

		let request: ReadLicensesAllRequest = {
			...params,
			...{
				userId: ctx.app.user.active()?.userId || ''
			}
		};
		if (request.userId === '') return [];

		let entities: LicenseEntities = await readLicensesAll(ctx, request)
			.then((entities: LicenseEntities) => {
				if (callback) callback(entities);
				return entities;
			})
			.catch(e => {
				if (callback) callback(e);
				return [];
			});
		return entities;
	}

	async write(
		ctx: UseCtx<any>,
		params: Partial<WriteLicensesRequest> = {},
		callback?: any
	): Promise<LicenseEntities> {
		if (!ctx.app.user.active()?.userId) return [];
		if (!params.licenses) return [];
		let request: WriteLicensesRequest = {
			...params,
			...{
				licenses: params.licenses,
				userId: ctx.app.user.active()?.userId || ''
			}
		};
		if (request.userId === '') return [];

		let entities: LicenseEntities = await writeLicenses(ctx, request)
			.then((entities: LicenseEntities) => {
				if (callback) callback();
				return entities;
			})
			.catch(e => {
				if (callback) callback(e);
				return [];
			});
		return entities;
	}

	allByContexts(
		contexts?: ContextEntities,
		services?: ServiceEntities
	): LicenseEntities {
		let contextIds = contexts?.map(context => context.id);
		if (contextIds && contextIds.length > 0) {
			return (services ? this.allByServices(services) : this.all())
				.filter(license => contextIds?.includes(license.contextId))
				.filter(isObjectStatusActive);
		} else {
			return [];
		}
	}

	allByServices(services: ServiceEntities): LicenseEntities {
		let serviceIds = services.map(services => services.id);
		if (serviceIds.length > 0) {
			return this.all()
				.filter(isObjectStatusActive)
				.filter(license => serviceIds.includes(license.serviceId));
		} else {
			return [];
		}
	}

	allByContextId(contextId?: string): LicenseEntities {
		if (contextId !== undefined && contextId.length > 0) {
			return this.all()
				.filter(isObjectStatusActive)
				.filter(license => license.contextId === contextId);
		} else {
			return [];
		}
	}

	allByOrderId(orderId?: string): LicenseEntities {
		if (orderId !== undefined && orderId.trim() !== '' && orderId.length > 0) {
			return this.all()
				.filter(isObjectStatusActive)
				.filter(license => license.orderId == orderId);
		} else {
			return [];
		}
	}

	licenseTypeToString(type?: LicenseServiceType): string {
		if (type === 'lead-api') {
			return 'Lead Api';
		} else if (type === 'lead-app') {
			return 'Lead App';
		} else if (type === 'opticon') {
			return 'Opticon';
		} else if (type === 'sst') {
			return 'SST';
		} else {
			return 'Item';
		}
	}

	async sendLicensesEmail(
		ctx: UseCtx<any>,
		params: {
			to: string;
			contextId: string;
			serviceId: string;
			eventName?: string;
			cc?: string;
			bcc?: string;
		},
		onError?: (message: string) => void,
		onSuccess?: (result: any) => void
	): Promise<any> {
		const { to, contextId, serviceId, eventName, cc, bcc } = params;

		const licensesUrl = `https://leads.conexsys.com/viewLicenses/${contextId}/${serviceId}`;
		const safeEventName = eventName || 'your event';

		const html = `<div style="background:#f5f7fa;padding:0;margin:0;font-family:'ProximaNova',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="margin:0;padding:0;border-collapse:collapse;">
    <tr>
      <td align="center" style="padding:24px;">
        <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0;">
          <tr>
            <td style="padding:0;">
              <img src="https://my.conexsys.com/clients/ui/img/mail/clo-email-header.png"
                   alt="CONEXSYS Lead Retrieval"
                   style="display:block;width:100%;max-width:600px;height:auto;" />
            </td>
          </tr>
          <tr>
            <td style="padding:32px;">
              <h1 style="margin:0 0 12px 0;font-size:24px;font-weight:600;color:#111827;">
                You’re invited to use CONEXSYS Lead Retrieval
              </h1>
              <p style="margin:0 0 16px 0;color:#374151;font-size:15px;line-height:1.5;">
                You have been invited to access lead retrieval services for <strong>${safeEventName}</strong>.
              </p>
              <p style="margin:0 0 20px 0;color:#374151;font-size:15px;line-height:1.5;">
                Use the button below to view your licenses and follow the getting started instructions.
              </p>
              <p style="text-align:center;margin:0 0 28px 0;">
                <a href="${licensesUrl}" target="_blank"
                   style="display:inline-block;background:#2563eb;color:#ffffff;text-decoration:none;
                          font-weight:600;padding:14px 28px;font-size:15px;border-radius:6px;">
                  View Licenses & Get Started
                </a>
              </p>
              <div style="margin:0 0 24px 0;">
                <h2 style="margin:0 0 12px 0;font-size:18px;color:#111827;">Next Steps</h2>
                <ol style="margin:0;padding-left:20px;color:#374151;font-size:14px;line-height:1.6;">
                  <li>Click the button above to open the licenses page.</li>
                  <li>Your licenses will appear automatically.</li>
                  <li>Follow on‑screen instructions to begin capturing leads.</li>
                </ol>
              </div>
              <p style="margin:0 0 32px 0;color:#6b7280;font-size:13px;line-height:1.5;">
                Trouble with the button? Copy and paste this link into your browser:<br>
                <a href="${licensesUrl}" target="_blank" style="color:#2563eb;text-decoration:none;">${licensesUrl}</a>
              </p>
              <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 24px 0;">
              <p style="margin:0 0 8px 0;color:#6b7280;font-size:12px;text-align:center;">
                If you did not expect this email, please contact <a href="https://conexsys.com/lead-support" target="_blank" style="color:#2563eb;text-decoration:none;">CONEXSYS Support</a>.
              </p>
              <p style="margin:0;color:#9ca3af;font-size:11px;text-align:center;">
                © ${new Date().getFullYear()} CONEXSYS. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</div>`;

		try {
			const response = await ctx.app.api.sendEmail({
				from: 'CONEXSYS Lead Retrieval <no-reply@conexsysleads.com>',
				sender: 'no-reply@conexsysleads.com',
				to,
				cc,
				bcc,
				subject: `${safeEventName} - Lead Retrieval Invitation`,
				html
			});

			if (!response) throw 'Response missing from call';

			if (response.status < 200 || response.status >= 300) {
				onError && onError(`${response.status}: Email Send Invalid Response`);
			} else {
				onSuccess && onSuccess(response.data);
			}
			return response.data;
		} catch (e) {
			console.error(e);
			onError && onError(`Error attempting to send email: ${e?.message || e}`);
		}
	}
}
